import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Plus, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CreatableCombobox } from '@/components/shared/CreatableCombobox';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { ShelfPositionGrid } from '@/components/shared/ShelfPositionGrid';
import { FormSection } from '@/components/shared/FormSection';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import type { Item, Shelf, Branch, Scanner } from '@packages/shared-types';
import {
  getShelfById,
  getShelfItems,
  getShelfCountResult,
  getShelfLocationOptions,
  type UpdateShelfPayload,
  type ShelfLocationOptions,
  updateShelf,
  deleteShelf,
} from '@/services/shelfService';
import { listBranches } from '@/services/branchService';
import { listScanners } from '@/services/scannerService';

const shelfFormSchema = z.object({
  name: z.string().min(1, 'pages.shelves.form.nameRequired'),
  branchId: z.string().uuid().min(1, 'pages.shelves.form.branchRequired'),
  scannerId: z.string().uuid().optional().or(z.literal('')),
  zone: z.string().optional(),
  aisle: z.string().optional(),
  module: z.string().optional(),
  row: z.string().optional(),
  cell: z.string().optional(),
  positionCells: z.array(z.string()).optional(),
});

type ShelfFormValues = z.infer<typeof shelfFormSchema>;

const LOCATION_PIECE_ORDER = [
  'zone',
  'aisle',
  'module',
  'row',
  'cell',
] as const;
type LocationPieceType = (typeof LOCATION_PIECE_ORDER)[number];

const MULTI_VALUE_PIECE_TYPES: LocationPieceType[] = ['row', 'cell'];
const SINGLE_VALUE_PIECE_TYPES: LocationPieceType[] = ['zone', 'aisle', 'module'];

function parseMultiValue(s: string | undefined): string[] {
  if (!s?.trim()) return [];
  return s.split(',').map((x) => x.trim());
}

function formatMultiValue(parts: string[]): string {
  return parts.filter(Boolean).join(',');
}

export function EditShelfPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editTitle = useIndustryTranslation('pages.shelves.edit.title');
  const editDescription = useIndustryTranslation('pages.shelves.edit.description');
  const deleteDialogTitle = useIndustryTranslation('pages.shelves.dialogs.delete.title');
  const loadingLabel = useIndustryTranslation('pages.shelves.list.loading');
  const successUpdate = useIndustryTranslation('pages.shelves.success.update');
  const successDelete = useIndustryTranslation('pages.shelves.success.delete');
  const errorLoad = useIndustryTranslation('pages.shelves.error.load');
  const errorUpdate = useIndustryTranslation('pages.shelves.error.update');
  const errorDelete = useIndustryTranslation('pages.shelves.error.delete');
  const errorNotFound = useIndustryTranslation('pages.shelves.error.notFound');
  const branchLabel = useIndustryTranslation('pages.shelves.form.branch');
  const zoneLabel = t('pages.shelves.form.zone');
  const aisleLabel = useIndustryTranslation('pages.shelves.fields.aisle');
  const moduleLabel = useIndustryTranslation('pages.shelves.fields.module');
  const rowLabel = useIndustryTranslation('pages.shelves.fields.row');
  const cellLabel = useIndustryTranslation('pages.shelves.fields.cell');
  const addLocationPieceLabel = t('pages.shelves.form.addLocationPiece');
  const addRowLabel = useIndustryTranslation('pages.shelves.form.addRow');
  const addCellLabel = useIndustryTranslation('pages.shelves.form.addCell');
  const shelfPositionsTitle = useIndustryTranslation(
    'pages.shelves.form.shelfPositions',
  );
  const shelfPositionsEmpty = useIndustryTranslation(
    'pages.shelves.form.shelfPositionsEmpty',
  );
  const removeRowLabel = useIndustryTranslation(
    'pages.shelves.form.removeRow',
  );
  const removeCellLabel = useIndustryTranslation(
    'pages.shelves.form.removeCell',
  );
  const editRowLabel = t('pages.shelves.form.editRow');
  const editCellLabel = t('pages.shelves.form.editCell');
  const locationEmptyOptions = t('pages.shelves.form.locationEmptyOptions');
  const locationPressEnterToAdd = t('pages.shelves.form.locationPressEnterToAdd');
  const detectedLabel = t('pages.shelves.countResult.detected');
  const notDetectedLabel = useIndustryTranslation(
    'pages.shelves.countResult.notDetected',
  );

  const hasHadPositionCellsRef = useRef(false);

  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [shelfItems, setShelfItems] = useState<Item[]>([]);
  const [countResult, setCountResult] = useState<{
    completedAt: string;
    detectedItemIds: string[];
    scannerId: string;
  } | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingScanners, setIsLoadingScanners] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [locationOptions, setLocationOptions] =
    useState<ShelfLocationOptions | null>(null);
  const [addedPieceTypes, setAddedPieceTypes] = useState<LocationPieceType[]>(
    [],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ShelfFormValues>({
    resolver: zodResolver(shelfFormSchema),
    defaultValues: {
      name: '',
      branchId: '',
      scannerId: '',
      zone: '',
      aisle: '',
      module: '',
      row: '',
      cell: '',
      positionCells: [] as string[],
    },
  });

  const selectedBranchId = watch('branchId');
  const zoneValue = watch('zone');
  const aisleValue = watch('aisle');
  const moduleValue = watch('module');
  const rowValue = watch('row');
  const cellValue = watch('cell');
  const positionCellsValue = watch('positionCells') ?? [];

  const locationLabels: Record<LocationPieceType, string> = useMemo(
    () => ({
      zone: zoneLabel,
      aisle: aisleLabel,
      module: moduleLabel,
      row: rowLabel,
      cell: cellLabel,
    }),
    [zoneLabel, aisleLabel, moduleLabel, rowLabel, cellLabel],
  );

  const locationValues: Record<LocationPieceType, string> = useMemo(
    () => ({
      zone: zoneValue ?? '',
      aisle: aisleValue ?? '',
      module: moduleValue ?? '',
      row: rowValue ?? '',
      cell: cellValue ?? '',
    }),
    [zoneValue, aisleValue, moduleValue, rowValue, cellValue],
  );

  const visiblePieceTypes = useMemo<LocationPieceType[]>(
    () => {
      const base = LOCATION_PIECE_ORDER.filter(
        (t) =>
          (locationValues[t]?.trim() ?? '') !== '' ||
          addedPieceTypes.includes(t),
      );
      if ((positionCellsValue?.length ?? 0) > 0) {
        return [...new Set([...base, 'row', 'cell'])] as LocationPieceType[];
      }
      return base;
    },
    [locationValues, addedPieceTypes, positionCellsValue],
  );

  const showPositionGrid =
    visiblePieceTypes.includes('row') && visiblePieceTypes.includes('cell');

  useEffect(() => {
    if ((positionCellsValue?.length ?? 0) > 0) {
      hasHadPositionCellsRef.current = true;
    }
  }, [positionCellsValue?.length]);

  useEffect(() => {
    if (
      !showPositionGrid ||
      (positionCellsValue?.length ?? 0) > 0 ||
      hasHadPositionCellsRef.current
    )
      return;
    const r = parseMultiValue(rowValue);
    const c = parseMultiValue(cellValue);
    if (r.length === 0 || c.length === 0) return;
    const derived = r.flatMap((row) =>
      c.map((cell) => `${row}-${cell}`),
    );
    setValue('positionCells', derived);
  }, [
    showPositionGrid,
    positionCellsValue?.length,
    rowValue,
    cellValue,
    setValue,
  ]);

  const availableToAddTypes = useMemo(
    () =>
      LOCATION_PIECE_ORDER.filter(
        (t) =>
          (locationValues[t]?.trim() ?? '') === '' &&
          !addedPieceTypes.includes(t),
      ),
    [locationValues, addedPieceTypes],
  );

  const locationOptionsByType: Record<
    LocationPieceType,
    string[]
  > = useMemo(
    () => ({
      zone: locationOptions?.zones ?? [],
      aisle: locationOptions?.aisles ?? [],
      module: locationOptions?.modules ?? [],
      row: locationOptions?.rows ?? [],
      cell: locationOptions?.cells ?? [],
    }),
    [locationOptions],
  );

  /** Rows/cells derived from current positionCells so "Add row/cell" dropdowns show newly added values */
  const gridRowOptions = useMemo(() => {
    const fromGrid = new Set<string>();
    for (const key of positionCellsValue ?? []) {
      const idx = key.indexOf('-');
      if (idx > 0) fromGrid.add(key.slice(0, idx).trim());
      else if (key.trim()) fromGrid.add(key.trim());
    }
    const merged = new Set([
      ...(locationOptionsByType.row ?? []),
      ...fromGrid,
    ]);
    return [...merged].sort((a, b) => a.localeCompare(b));
  }, [positionCellsValue, locationOptionsByType.row]);

  const gridCellOptions = useMemo(() => {
    const fromGrid = new Set<string>();
    for (const key of positionCellsValue ?? []) {
      const idx = key.indexOf('-');
      if (idx > 0) fromGrid.add(key.slice(idx + 1).trim());
    }
    const merged = new Set([
      ...(locationOptionsByType.cell ?? []),
      ...fromGrid,
    ]);
    return [...merged].sort((a, b) => a.localeCompare(b));
  }, [positionCellsValue, locationOptionsByType.cell]);

  const placeholders: Record<LocationPieceType, string> = useMemo(
    () => ({
      zone: t('pages.shelves.form.placeholderZone'),
      aisle: t('pages.shelves.form.placeholderAisle'),
      module: t('pages.shelves.form.placeholderModule'),
      row: t('pages.shelves.form.placeholderRow'),
      cell: t('pages.shelves.form.placeholderCell'),
    }),
    [t],
  );

  const displayedRows = useMemo(() => {
    const parts = parseMultiValue(rowValue);
    if (parts.length === 0 && visiblePieceTypes.includes('row')) return [''];
    return parts.length ? parts : [''];
  }, [rowValue, visiblePieceTypes]);

  const displayedCells = useMemo(() => {
    const parts = parseMultiValue(cellValue);
    if (parts.length === 0 && visiblePieceTypes.includes('cell')) return [''];
    return parts.length ? parts : [''];
  }, [cellValue, visiblePieceTypes]);

  const handleRemoveLocationPiece = useCallback(
    (type: LocationPieceType) => {
      setValue(type, '');
      setAddedPieceTypes((prev) => prev.filter((t) => t !== type));
    },
    [setValue],
  );

  const handleAddLocationPiece = useCallback((type: LocationPieceType) => {
    setAddedPieceTypes((prev) => (prev.includes(type) ? prev : [...prev, type]));
  }, []);

  // Filter scanners by selected branch
  const availableScanners = useMemo(() => {
    if (!selectedBranchId) return [];
    return scanners.filter((scanner) => scanner.branchId === selectedBranchId);
  }, [scanners, selectedBranchId]);

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const loadShelf = useCallback(async () => {
    if (!id) return;
    hasHadPositionCellsRef.current = false;
    setIsLoading(true);
    setError(null);
    try {
      const [shelfData, branchesData, scannersData] = await Promise.all([
        getShelfById(id),
        listBranches(),
        listScanners(),
      ]);

      setShelf(shelfData);
      setBranches(branchesData);
      setScanners(scannersData);

      const fromPositionCells =
        (shelfData.positionCells?.length ?? 0) > 0
          ? shelfData.positionCells
          : null;
      const rawPositionCellsCol = (
        shelfData as Shelf & { position_cells?: string | null }
      ).position_cells?.trim();
      const fromPositionCellsColumn =
        !fromPositionCells && rawPositionCellsCol
          ? rawPositionCellsCol
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null;
      const positionCells =
        fromPositionCells ??
        (fromPositionCellsColumn && fromPositionCellsColumn.length > 0
          ? fromPositionCellsColumn
          : (() => {
              const r = parseMultiValue(shelfData.row ?? undefined);
              const c = parseMultiValue(shelfData.cell ?? undefined);
              if (r.length === 0 || c.length === 0) return [];
              return r.flatMap((row) =>
                c.map((cell) => `${row}-${cell}`),
              );
            })());

      reset({
        name: shelfData.name ?? '',
        branchId: shelfData.branchId ?? '',
        scannerId: shelfData.scannerId ?? '',
        zone: shelfData.zone ?? '',
        aisle: shelfData.aisle ?? '',
        module: shelfData.module ?? '',
        row: shelfData.row ?? '',
        cell: shelfData.cell ?? '',
        positionCells,
      });

      const typesWithValue = LOCATION_PIECE_ORDER.filter((key) => {
        const v = shelfData[key];
        return v != null && String(v).trim() !== '';
      });
      const withGrid =
        positionCells.length > 0
          ? ([...new Set([...typesWithValue, 'row', 'cell'])] as LocationPieceType[])
          : typesWithValue;
      setAddedPieceTypes(withGrid);

      try {
        const options = await getShelfLocationOptions(
          shelfData.branchId ?? undefined,
        );
        setLocationOptions(options);
      } catch {
        setLocationOptions(null);
      }

      setIsLoadingCount(true);
      try {
        const [itemsData, countData] = await Promise.all([
          getShelfItems(id),
          getShelfCountResult(id),
        ]);
        setShelfItems(itemsData);
        setCountResult(
          countData
            ? {
                completedAt: countData.completedAt,
                detectedItemIds: countData.detectedItemIds,
                scannerId: countData.scannerId,
              }
            : null,
        );
      } catch {
        setShelfItems([]);
        setCountResult(null);
      } finally {
        setIsLoadingCount(false);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad]);

  useEffect(() => {
    void loadShelf();
  }, [loadShelf]);

  useEffect(() => {
    if (!selectedBranchId) return;
    getShelfLocationOptions(selectedBranchId)
      .then(setLocationOptions)
      .catch(() => setLocationOptions(null));
  }, [selectedBranchId]);

  // Load branches and scanners on mount
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setIsLoadingBranches(true);
      setIsLoadingScanners(true);
      try {
        const [branchesData, scannersData] = await Promise.all([
          listBranches(),
          listScanners(),
        ]);
        setBranches(branchesData);
        setScanners(scannersData);
      } catch (err) {
        console.error('Failed to load data', err);
      } finally {
        setIsLoadingBranches(false);
        setIsLoadingScanners(false);
      }
    };
    void loadData();
  }, []);

  const handleSubmitShelf = async (
    values: ShelfFormValues,
  ): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const norm = (x: string | undefined): string | undefined =>
      x?.trim() ? x.trim().toUpperCase() : undefined;
    const normMulti = (x: string | undefined): string | undefined => {
      const parts = parseMultiValue(x)
        .filter(Boolean)
        .map((p) => p.toUpperCase());
      return parts.length ? parts.join(',') : undefined;
    };
    const normPositionCells = (arr: string[] | undefined): string[] | undefined => {
      if (!arr?.length) return undefined;
      const out = arr
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => p.toUpperCase());
      return out.length ? out : undefined;
    };

    const payload: UpdateShelfPayload = {
      name: values.name,
      branchId: values.branchId,
      scannerId: values.scannerId || undefined,
      zone: norm(values.zone),
      aisle: norm(values.aisle),
      module: norm(values.module),
      row: showPositionGrid ? undefined : normMulti(values.row),
      cell: showPositionGrid ? undefined : normMulti(values.cell),
      positionCells: showPositionGrid
        ? normPositionCells(values.positionCells)
        : undefined,
    };

    try {
      await updateShelf(id, payload);
      await loadShelf();
      toast.success(successUpdate);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !shelf) return;
    setIsDeleting(true);
    try {
      await deleteShelf(id);
      toast.success(successDelete);
      navigate('/shelves');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitShelf)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {errorNotFound}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={shelf ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="shelf-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.shelves.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitShelf)()}
      saveLabel={t('pages.shelves.actions.update')}
      savingLabel={t('pages.shelves.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.shelves.actions.delete')}
      deletingLabel={t('pages.shelves.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.shelves.dialogs.delete.description', {
        name: shelf?.name,
      })}
      entityExists={!!shelf}
    >
      <form
        id="shelf-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitShelf)}
      >
        <FormSection
          title={t('pages.shelves.form.name')}
          description={t('pages.shelves.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="shelf-name"
            {...register('name')}
            placeholder={t('pages.shelves.form.name')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection
          title={branchLabel}
          description={t('pages.shelves.form.selectBranch')}
          error={
            errors.branchId
              ? formatErrorMessage(errors.branchId.message) ??
                (errors.branchId.message as string)
              : null
          }
        >
          <Controller
            name="branchId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  // Reset scanner when branch changes
                  setValue('scannerId', '');
                }}
                disabled={isSaving || isLoadingBranches}
              >
                <SelectTrigger id="shelf-branch">
                  <SelectValue
                    placeholder={t('pages.shelves.form.selectBranch')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.shelves.form.scannerOptional')}
          description={t('pages.shelves.form.selectScanner')}
        >
          <Controller
            name="scannerId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={(value) => field.onChange(value || '')}
                disabled={isSaving || isLoadingScanners || !selectedBranchId}
              >
                <SelectTrigger id="shelf-scanner">
                  <SelectValue
                    placeholder={t('pages.shelves.form.selectScanner')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableScanners.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {selectedBranchId
                        ? t('pages.shelves.form.noScanners')
                        : t('pages.shelves.form.selectBranchFirst')}
                    </SelectItem>
                  ) : (
                    availableScanners.map((scanner) => (
                      <SelectItem key={scanner.id} value={scanner.id}>
                        {scanner.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.shelves.form.locationTitle')}
          description={t('pages.shelves.form.locationDescriptionShort')}
        >
          <div className="flex flex-col gap-4">
            {availableToAddTypes.length > 0 && (
              <div className="flex items-center gap-2">
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (v && LOCATION_PIECE_ORDER.includes(v as LocationPieceType)) {
                      handleAddLocationPiece(v as LocationPieceType);
                    }
                  }}
                  disabled={isSaving}
                >
                  <SelectTrigger
                    id="add-location-piece"
                    className="w-full max-w-xs"
                  >
                    <SelectValue
                      placeholder={addLocationPieceLabel}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAddTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {locationLabels[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Zone, Aisle, Section: single row on top */}
            {visiblePieceTypes.filter((type): type is LocationPieceType =>
              SINGLE_VALUE_PIECE_TYPES.includes(type),
            ).length > 0 && (
              <div className="flex flex-wrap items-end gap-4">
                {visiblePieceTypes
                  .filter((type): type is LocationPieceType =>
                    SINGLE_VALUE_PIECE_TYPES.includes(type),
                  )
                  .map((type) => (
                    <div
                      key={type}
                      className="flex items-end gap-2 min-w-0 sm:min-w-[140px] flex-1"
                    >
                      <div className="min-w-0 flex-1 space-y-2">
                        <Label htmlFor={`shelf-location-${type}`}>
                          {locationLabels[type]}
                        </Label>
                        <Controller
                          name={type}
                          control={control}
                          render={({ field }) => (
                            <CreatableCombobox
                              id={`shelf-location-${type}`}
                              options={locationOptionsByType[type]}
                              value={field.value ?? ''}
                              onChange={(v) => field.onChange(v)}
                              placeholder={placeholders[type]}
                              disabled={isSaving}
                              emptyText={locationEmptyOptions}
                              createHint={locationPressEnterToAdd}
                            />
                          )}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={isSaving}
                        onClick={() => handleRemoveLocationPiece(type)}
                        aria-label={t(
                          'pages.shelves.form.removeLocationPiece',
                        )}
                        className="shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
              </div>
            )}
            {/* Row/Cell multi-value blocks (only when grid is not shown) */}
            {!showPositionGrid &&
              visiblePieceTypes
                .filter((type): type is LocationPieceType =>
                  MULTI_VALUE_PIECE_TYPES.includes(type),
                )
                .map((type) => (
                  <div
                    key={type}
                    className="flex flex-col gap-2 rounded-md border p-3 sm:min-w-[200px]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Label className="shrink-0">
                        {locationLabels[type]}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={isSaving}
                        onClick={() => handleRemoveLocationPiece(type)}
                        aria-label={t(
                          'pages.shelves.form.removeLocationPiece',
                        )}
                        className="h-8 w-8 shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                      {(type === 'row'
                        ? displayedRows
                        : displayedCells
                      ).map((val, i) => (
                        <div
                          key={`${type}-${i}`}
                          className="flex items-center gap-2"
                        >
                          <CreatableCombobox
                            id={`shelf-location-${type}-${i}`}
                            options={locationOptionsByType[type]}
                            value={val}
                            onChange={(v) => {
                              const arr =
                                type === 'row'
                                  ? [...displayedRows]
                                  : [...displayedCells];
                              arr[i] = v;
                              setValue(type, formatMultiValue(arr));
                            }}
                            placeholder={placeholders[type]}
                            disabled={isSaving}
                            emptyText={locationEmptyOptions}
                            createHint={locationPressEnterToAdd}
                            className="min-w-0 flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={isSaving}
                            onClick={() => {
                              const arr =
                                type === 'row'
                                  ? displayedRows.filter((_, j) => j !== i)
                                  : displayedCells.filter(
                                      (_, j) => j !== i,
                                    );
                              setValue(
                                type,
                                arr.length ? formatMultiValue(arr) : '',
                              );
                            }}
                            aria-label={t(
                              'pages.shelves.form.removeLocationPiece',
                            )}
                            className="h-8 w-8 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                        onClick={() => {
                          const current =
                            type === 'row'
                              ? rowValue ?? ''
                              : cellValue ?? '';
                          setValue(
                            type,
                            current ? `${current},` : '',
                          );
                        }}
                        className="w-fit"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {type === 'row' ? addRowLabel : addCellLabel}
                      </Button>
                    </div>
                  </div>
                ))}
            {/* Shelf position grid: full width below zone/aisle/section */}
            {showPositionGrid && (
              <div className="w-full">
                <Controller
                  name="positionCells"
                  control={control}
                  render={({ field }) => (
                    <ShelfPositionGrid
                      positionCells={field.value ?? []}
                      onChange={(v) => field.onChange(v)}
                      disabled={isSaving}
                      rowOptions={gridRowOptions}
                      cellOptions={gridCellOptions}
                      addRowLabel={addRowLabel}
                      addCellLabel={addCellLabel}
                      removeRowLabel={removeRowLabel}
                      removeCellLabel={removeCellLabel}
                      editRowLabel={editRowLabel}
                      editCellLabel={editCellLabel}
                      emptyText={shelfPositionsEmpty}
                      title={shelfPositionsTitle}
                      emptyOptionsText={locationEmptyOptions}
                      createHint={locationPressEnterToAdd}
                      rowPlaceholder={t('pages.shelves.form.rowLabelPlaceholder')}
                      cellPlaceholder={t('pages.shelves.form.cellLabelPlaceholder')}
                      confirmButtonLabel={t('pages.shelves.form.confirmAdd')}
                    />
                  )}
                />
              </div>
            )}
          </div>
        </FormSection>
      </form>
      {shelf && (
        <div className="mt-8 space-y-4">
          <h3 className="text-lg font-semibold">
            {t('pages.shelves.countResult.lastCount')}
          </h3>
          {isLoadingCount ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <LoadingLogo size="sm" />
              <span className="text-sm text-muted-foreground">{loadingLabel}</span>
            </div>
          ) : !countResult ? (
            <p className="text-sm text-muted-foreground">
              {t('pages.shelves.countResult.noCountYet')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {t('pages.shelves.countResult.completedAt', {
                  date: new Date(countResult.completedAt).toLocaleString(),
                })}
                {countResult.scannerId
                  ? ` · ${
                      scanners.find((s) => s.id === countResult.scannerId)
                        ?.name ?? ''
                    }`
                  : ''}
              </p>
              <p className="text-sm font-medium">
                {t('pages.shelves.countResult.totalDetected', {
                  count: countResult.detectedItemIds.length,
                })}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pages.shelves.countResult.itemName')}</TableHead>
                    <TableHead>{t('pages.shelves.countResult.sku')}</TableHead>
                    <TableHead>{t('pages.shelves.countResult.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shelfItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center text-muted-foreground"
                      >
                        {t('pages.shelves.countResult.noItemsOnShelf')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    shelfItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.name}
                        </TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell>
                          {countResult.detectedItemIds.includes(item.id) ? (
                            <StatusBadge variant="success">
                              {detectedLabel}
                            </StatusBadge>
                          ) : (
                            <StatusBadge variant="danger">
                              {notDetectedLabel}
                            </StatusBadge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      )}
    </EditPageLayout>
  );
}
